// First, as it was webpack's first entry: font-awesome and the one shared moment instance.
// See the file.
import './vendor';

// Froala's stylesheets load with the app, at this place in the cascade. Its script and its
// plugins load the first time an editor renders: app/react/shared/froala/.
import 'froala-editor/css/froala_editor.pkgd.css';
import 'froala-editor/css/plugins/colors.min.css';

// The base layer, where Bootstrap's stylesheet was: the few Reboot rules the CMS relies on,
// in the same place in the cascade. See its header.
import './css/base.css';

// Colour tokens must be defined before the stylesheets that reference them.
import './css/tokens.css';
import './css/main.css';
import './css/simple-sidebar.css';
// The shared form fields, the toggle controls and the editor layout. After main.css so they
// win at equal specificity, before Tailwind so a utility still wins over them.
import './css/fields.css';
import './css/controls.css';
import './css/editor.css';
// Last on purpose: see the header of css/tailwind.css.
import './css/tailwind.css';

import { initializeFaro, getWebInstrumentations } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { B3Propagator } from '@opentelemetry/propagator-b3';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';

if (process.env.TELEMETRY_ENABLED !== 'false') {
  initializeFaro({
    // Mandatory, the URL of the Grafana Cloud collector with embedded application key.
    // Copy from the configuration page of your application in Grafana.
    url: process.env.TELEMETRY_URL,
    apiKey: process.env.TELEMETRY_KEY,

    // Mandatory, the identification label(s) of your application
    app: {
      name: 'cmb-cms-web',
      version: '0.3.0',
      environment: 'dev',
    },

    instrumentations: [
      // Don't forget to add the other instrumentations as well
      ...getWebInstrumentations(),

      new TracingInstrumentation({
        // Optional, if you want to replace the default W3C Trace Context Propagator
        propagator: new B3Propagator(),

        instrumentations: [
          new XMLHttpRequestInstrumentation({
            propagateTraceHeaderCorsUrls: [/https:\/\/.*\.combobulate\.(io|com).*/],
          }),
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls: [/https:\/\/.*\.combobulate\.(io|com).*/],
          }),
          new DocumentLoadInstrumentation(),
          new UserInteractionInstrumentation(),
        ],
      }),
    ],
  });
}

/*
 * jQuery and moment on `window`. Froala's global is set by its own module, which loads on
 * first use (app/react/shared/froala/FroalaEditorWithPlugins.js).
 *
 * These are left as globals rather than cleaned up with the rest of the Angular bootstrap.
 * Froala 4 does not need jQuery (its plugins use the editor's own `$` helper), but
 * `app/react/shared/util/froalaUtils.js` still calls the bare `$` when a WYSIWYG page is
 * saved, and a tenant's own uploaded script could read any of them.
 *
 * They are the only globals left. webpack's `ProvidePlugin` also used to hand `_` to three
 * React files that never imported lodash; Vite has no equivalent, so since SURF-4808 they
 * import it, and ESLint's `no-undef` keeps it that way.
 */
import $ from 'jquery';
import moment from 'moment';
import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './app/react/App';
import { reloadOnStaleChunk } from './app/react/shared/util/lazyLoad';

(window as any).jQuery = $;
(window as any).$ = $;
(window as any).moment = moment;

// A tab left open across a deploy reloads once when it asks for a chunk that is gone.
reloadOnStaleChunk();

const container = document.getElementById('root');
if (!container) throw new Error('No #root element; check web/index.html.');
createRoot(container).render(React.createElement(App));
